import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import debounce from "lodash.debounce";

import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";

import {
  selectCategories,
  selectCollectionDetails,
  selectExploreCollections,
  selectExploreCollectionsFilter,
  selectFavorites,
  selectGenerateCollection,
  setSelectedExploreCollectionFilter,
} from "../../store/slices/model/slice";
import {
  fetchCollection,
  getExploreCollections,
  getUserFavorites,
} from "../../store/slices/model/asyncThunks";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { isImageOutput } from "../../utils/utils";
import { MEDIA_TYPE } from "../../utils/constants";

import Modal from "../utils/Modal/Modal";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import CollectionFilter from "../CollectionFilter/CollectionFilter";
import VideoMasonrySkeletonLoader from "../utils/VideoMasonrySkeletonLoader";
import ExploreItem from "../ExploreItem/ExploreItem";
import Pagination from "../Pagination/Pagination";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import checkboxAnimation from "../../assets/animations/checkBox.json";
import settingsAnimation from "../../assets/animations/settings.json";
import ExplosionButton from "../ExplosionButton/ExplosionButton";

import {
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  Container,
  ContentContainer,
  Content,
  Column,
  EmptyState,
  PaginationWrapper,
  CheckboxContainer,
  Footer,
  ActionRow,
  SettingsButton,
  InfoButton,
  TooltipContainer,
  TooltipText,
  CancelButton,
} from "./ExtendVideoModal.styles";
import {
  GenerateLoader,
  GenerateLoaderDots,
  GenerateLoaderSparkle,
} from "../GenerateCollectionModal/GenerateCollectionModal.styles";
import { GenerateDrop } from "../CreateInputBar/CreateInputBar.styles";

const ExtendVideoModal = ({
  isOpen,
  onClose,
  onConfirm,
  confirmButtonType = "primary",
  setIsEnterPromptModalOpen,
  setSelectedModel,
  setExtendVideoPrice,
  selectedCollectionVideo,
  totalPriceVideoExtend,
  setSelectedExtendCollection,
}) => {
  const dispatch = useDispatch();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [page, setPageState] = useState(1);
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState(() => "");

  const filter = useSelector(selectExploreCollectionsFilter);
  const exploreData = useSelector(selectExploreCollections);
  const favoriteData = useSelector(selectFavorites);
  const loggedIn = useSelector(selectLoggedIn);
  const { data: categories } = useSelector(selectCategories);

  const isDesktop = useMediaQuery({ maxWidth: 1224 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const contentContainerRef = useRef(null);
  const { loading: isLoading } = useSelector(selectGenerateCollection);
  const { data: collectionDetailsData, loading: extendLoading } = useSelector(
    selectCollectionDetails,
  );

  useLockBodyScroll(isOpen);

  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      setIsExploding(true);
      const t = setTimeout(() => setIsExploding(false), 600);
      prevLoadingRef.current = isLoading;
      return () => clearTimeout(t);
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  // ── Derived state ──

  const numOfColumns = useMemo(() => {
    if (isMobile) return 2;
    if (isTable) return 5;
    if (isDesktop) return 6;
    return 7;
  }, [isDesktop, isTable, isMobile]);

  const data = useMemo(() => {
    if (filter === "FAVORITES") return favoriteData?.data || [];
    if (filter === "EXTEND_WITH") {
      const extendItems = collectionDetailsData?.extend || [];
      const query = (search || "").trim().toLowerCase();
      if (!query) return extendItems;
      return extendItems.filter((item) => {
        const name = item?.name?.toLowerCase() || "";
        const username = item?.username?.toLowerCase() || "";
        const tagNames = (item?.tags || [])
          .map((t) => t?.name?.toLowerCase() || "")
          .join(" ");
        return (
          name.includes(query) ||
          username.includes(query) ||
          tagNames.includes(query)
        );
      });
    }
    return exploreData?.data || [];
  }, [filter, favoriteData, exploreData, collectionDetailsData, search]);

  const total = useMemo(() => {
    if (filter === "FAVORITES") return favoriteData?.total || 0;
    if (filter === "EXTEND_WITH") return data?.length || 0;
    return exploreData?.total || 0;
  }, [filter, favoriteData, exploreData, data]);

  const loading = useMemo(() => {
    if (filter === "FAVORITES") return favoriteData.loading;
    if (filter === "EXTEND_WITH") return extendLoading;
    return exploreData.loading;
  }, [filter, favoriteData, exploreData, extendLoading]);

  const isImage = selectedCollectionVideo?.output?.type === MEDIA_TYPE.IMAGE;

  // ── Data fetching effects ──

  useEffect(() => {
    if (categories?.length && !filter) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
    if (!categories?.length) {
      dispatch(getExploreCollections({ page: 1, search: "" }));
    }
  }, [categories, dispatch, filter]);

  useEffect(() => {
    if (!loggedIn && filter === "FAVORITES" && categories?.length) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
  }, [loggedIn, filter, categories, dispatch]);

  useEffect(() => {
    if (filter === "FAVORITES" && !data) {
      setPageState(1);
      dispatch(getUserFavorites({ page: 1, search: "" }));
    }
  }, [filter, dispatch, data]);

  useEffect(() => {
    if (!filter || data) return;
    if (filter !== "FAVORITES") {
      setPageState(1);
      dispatch(
        getExploreCollections({ page: 1, search: "", categoryId: filter }),
      );
    }
  }, [dispatch, filter, data]);

  useEffect(() => {
    if (filter === "EXTEND_WITH" && data?.length === 0 && isOpen) {
      const trendingCategory = categories?.find((c) => c.name === "Trending");
      const fallbackFilter = trendingCategory?._id ?? categories?.[0]?._id;
      if (fallbackFilter) {
        dispatch(setSelectedExploreCollectionFilter(fallbackFilter));
        dispatch(
          getExploreCollections({
            page: 1,
            search: "",
            categoryId: fallbackFilter,
          }),
        );
      }
    }
  }, [filter, data, categories, dispatch, search, isOpen]);

  // ── Handlers ──

  const onChangeFilter = (newFilter) => {
    dispatch(setSelectedExploreCollectionFilter(newFilter));
    setPageState(1);

    if (newFilter === "FAVORITES") {
      dispatch(getUserFavorites({ page: 1, search }));
    } else if (newFilter === "EXTEND_WITH") {
      if (selectedCollectionVideo?._id) {
        dispatch(
          fetchCollection({ collectionId: selectedCollectionVideo._id }),
        );
      }
    } else {
      dispatch(
        getExploreCollections({ page: 1, search, categoryId: newFilter }),
      );
    }
  };

  const changeHandler = useCallback(
    (text) => {
      setPageState(1);
      if (search !== text) {
        setSearch(text);
        if (filter === "EXTEND_WITH") {
          // Search is filtered locally for the extend list; no API call needed
          return;
        }
        if (filter !== "FAVORITES") {
          dispatch(
            getExploreCollections({
              page: 1,
              search: text,
              categoryId: filter,
            }),
          );
        } else {
          dispatch(getUserFavorites({ page: 1, search: text }));
        }
      }
    },
    [dispatch, filter, search],
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler],
  );

  const setPage = (newPage) => {
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTo({ top: 0, behavior: "instant" });
    }
    setPageState(newPage);
    if (filter !== "FAVORITES") {
      dispatch(
        getExploreCollections({ page: newPage, search, categoryId: filter }),
      );
    } else {
      dispatch(getUserFavorites({ page: newPage, search }));
    }
  };

  const resetStates = () => {
    setPageState(1);
    setSearchValue("");
    if (categories?.length) {
      const defaultFilter = categories[0]._id;
      dispatch(setSelectedExploreCollectionFilter(defaultFilter));
      dispatch(
        getExploreCollections({
          page: 1,
          search: "",
          categoryId: defaultFilter,
        }),
      );
    }
  };

  const handleConfirm = () => onConfirm();

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
      resetStates();
    }
  };

  if (!selectedCollectionVideo) return null;

  // ── Render ──

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      shouldCloseOnOverlayClick={false}
      customOverlayStyles={{ zIndex: 3100 }}
    >
      <ModalContainer>
        {/* ── Header ── */}
        <ModalHeader>
          <ModalTitle>{isImage ? "Animate Image" : "Extend Video"}</ModalTitle>
          <CloseButton onClick={handleCancel}>
            <CloseSvg />
          </CloseButton>
        </ModalHeader>

        {/* ── Body ── */}
        <ModalBody>
          <CollectionFilter
            minHeight
            offHorizontalPadding
            searchPlaceholderText="title, username or tags"
            filter={filter}
            setFilter={onChangeFilter}
            searchValue={searchValue}
            onSearchChange={(value) => {
              setSearchValue(value);
              debouncedChangeHandler(value);
            }}
            hidePicturesCategory
            isExtendModal
            extendData={collectionDetailsData?.extend?.length > 0}
          />

          <Container>
            <ContentContainer ref={contentContainerRef}>
              {loading || !data ? (
                <VideoMasonrySkeletonLoader count={24} mobileTwoRows />
              ) : (
                <Content>
                  {!data?.length ? (
                    <EmptyState>
                      <h3>No videos found</h3>
                      <p>There are no videos to display at the moment.</p>
                    </EmptyState>
                  ) : (
                    Array.from({ length: numOfColumns }).map((_, colIndex) => (
                      <Column key={colIndex}>
                        {data
                          .filter((item) => !isImageOutput(item.modelName))
                          .filter((_, i) => i % numOfColumns === colIndex)
                          .map((item) => {
                            const isSelected = selectedVideoId === item._id;

                            return (
                              <ExploreItem
                                key={item._id}
                                item={item}
                                isMobile={isMobile}
                                offGenerate
                                onItemClick={() => {
                                  if (loggedIn) {
                                    if (isSelected) {
                                      setSelectedVideoId(null);
                                      setSelectedModel(null);
                                      setSelectedExtendCollection(null);
                                    } else {
                                      setSelectedVideoId(item._id);
                                      setSelectedModel(item.model);
                                      setExtendVideoPrice(item.price);
                                      setSelectedExtendCollection(item);
                                    }
                                  } else {
                                    dispatch(setSignUpModalOpen(true));
                                  }
                                }}
                                extraContent={
                                  <CheckboxContainer>
                                    <LottieIcon
                                      animationData={checkboxAnimation}
                                      trigger="toggle-reverse"
                                      isActive={isSelected}
                                      strokeColor={"#fff"}
                                      size={18}
                                      speed={2}
                                    />
                                  </CheckboxContainer>
                                }
                              />
                            );
                          })}
                      </Column>
                    ))
                  )}
                </Content>
              )}
            </ContentContainer>

            {filter !== "EXTEND_WITH" && (
              <PaginationWrapper>
                <Pagination
                  small={isMobile}
                  currentPage={page}
                  totalCount={total}
                  perPage={20}
                  onPageChange={(p) => setPage(p)}
                />
              </PaginationWrapper>
            )}
          </Container>
        </ModalBody>

        {/* ── Footer ── */}
        <Footer>
          <ActionRow>
            <SettingsButton
              onClick={() => {
                setIsEnterPromptModalOpen(true);
                localStorage.setItem("wasPromptModalOpen", true);
              }}
            >
              <LottieIcon
                animationData={settingsAnimation}
                trigger="hover"
                size={24}
                reverseSpeed={4}
                strokeColor={"white"}
              />
            </SettingsButton>

            <ExplosionButton
              style={{ flex: 1, width: "100%", minWidth: 0 }}
              onClick={handleConfirm}
              disabled={isLoading || !selectedVideoId}
              isLoading={isLoading}
              isExploding={isExploding}
            >
              {isLoading ? (
                <GenerateLoader>
                  <span>Generating</span>
                  <GenerateLoaderDots>
                    <span />
                    <span />
                    <span />
                  </GenerateLoaderDots>
                  <GenerateLoaderSparkle>💦</GenerateLoaderSparkle>
                </GenerateLoader>
              ) : (
                <>
                  Generate
                  {totalPriceVideoExtend && (
                    <>
                      <GenerateDrop aria-hidden="true" />
                      {Math.ceil(totalPriceVideoExtend)}
                    </>
                  )}
                </>
              )}
            </ExplosionButton>

            <InfoButton
              onMouseEnter={() => !isMobile && setShowTooltip(true)}
              onMouseLeave={() => !isMobile && setShowTooltip(false)}
              onClick={() => isMobile && setShowTooltip(!showTooltip)}
            >
              <InfoIcon width={22} height={22} />
              {showTooltip && (
                <TooltipContainer>
                  <TooltipText>
                    {isImage
                      ? "The generated image will be used as the first frame of the generated video. For best results, match the original image. The AI follows basic physics — objects not present in the input image are unlikely to appear in the generated video."
                      : "Extending a video reuses its last frame as the starting point for the new generation, making the final video longer and seamless."}
                  </TooltipText>
                </TooltipContainer>
              )}
            </InfoButton>
          </ActionRow>

          <CancelButton onClick={handleCancel} disabled={isLoading}>
            Cancel
          </CancelButton>
        </Footer>
      </ModalContainer>
    </Modal>
  );
};

export default ExtendVideoModal;
